import React, { useEffect, useState, memo } from "react";
import { useTheme } from "@mui/material";
import { getHeadersWithUniqueVisitorId } from "@brandclub/common-ui";
import ConfigUtils from "@/utils/ConfigUtils";
import { PageContext } from "../hooks/usePageContext";
import { wrapSearchWithProductContext } from "../utils/wrapSearchWithProductContext";

interface ConciergeWelcomeEmbeddedProps {
  displayQueries: string[];
  isLoadingSuggestedQueries: boolean;
  onSearch: (searchTerm: string) => void;
  pageContext?: PageContext;
}

const ConciergeWelcomeEmbeddedComponent: React.FC<
  ConciergeWelcomeEmbeddedProps
> = ({ displayQueries, isLoadingSuggestedQueries, onSearch, pageContext }) => {
  const theme = useTheme();
  const [contextQueries, setContextQueries] = useState<string[]>([]);
  const [isLoadingContextQueries, setIsLoadingContextQueries] = useState(false);

  // Memoize the pageContext values to prevent unnecessary re-fetches
  const pageType = pageContext?.pageType;
  const stacklineSku = pageContext?.stacklineSku;
  
  // Fetch context-specific queries (e.g., product conversation starters for PDP)
  useEffect(() => {
    const fetchContextQueries = async () => {
      if (pageType === "pdp" && stacklineSku) {
        try {
          setIsLoadingContextQueries(true);
          const headers = await getHeadersWithUniqueVisitorId();
          const response = await fetch(
            `${ConfigUtils.getApiHostName()}/concierge/conversationStarters`,
            {
              method: "POST",
              headers: {
                ...headers,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                stacklineSku: stacklineSku,
              }),
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data?.questions && Array.isArray(data.questions)) {
              setContextQueries(data.questions);
            }
          }
        } catch (error) {
          console.error("Error fetching context conversation starters:", error);
        } finally {
          setIsLoadingContextQueries(false);
        }
      } else {
        setContextQueries([]);
      }
    };

    fetchContextQueries();
  }, [pageType, stacklineSku]);

  // Use context queries if available, otherwise fall back to displayQueries
  // Memoize to prevent recalculation on every render
  const queriesToShow = React.useMemo(
    () => (contextQueries.length > 0 ? contextQueries : displayQueries),
    [contextQueries, displayQueries]
  );
  const isLoading = React.useMemo(
    () =>
      isLoadingContextQueries ||
      (pageContext?.pageType === "pdp" && isLoadingSuggestedQueries),
    [isLoadingContextQueries, pageContext?.pageType, isLoadingSuggestedQueries]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        textAlign: "center",
        width: "100%",
        height: "100%",
        minHeight: "400px", // Prevent layout shift
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Compact heading for embedded mode */}
      <div
        style={{
          marginBottom: "20px",
        }}
      >
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "600",
            color: theme.palette.text.primary,
            margin: "0 0 8px 0",
          }}
        >
          How can I help you today?
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: theme.palette.text.secondary,
            margin: 0,
          }}
        >
          Ask me anything about products, deals, or recommendations
        </p>
      </div>

      {/* Compact suggestions for embedded mode */}
      {/* Remove dynamic key that causes remounting on every render */}
      {queriesToShow.length > 0 && !isLoading && (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxWidth: "100%",
          }}
        >
          {queriesToShow.slice(0, 4).map((query, index) => (
            <button
              key={index}
              onClick={() => {
                // Wrap query with product context if on a PDP
                const queryWithContext = wrapSearchWithProductContext(
                  query,
                  pageContext
                );
                onSearch(queryWithContext);
              }}
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 0.2s ease",
                width: "100%",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme.palette.action.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {query}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Memoize to prevent re-renders when parent re-renders due to input changes
export const ConciergeWelcomeEmbedded = memo(ConciergeWelcomeEmbeddedComponent);

